import Component from '@glimmer/component';
import { on } from '@ember/modifier';
import { fn } from '@ember/helper';
import { eq } from 'tio-ui/utilities';
import { get } from '@ember/helper';
import { PhX, PhFunnel } from 'ember-phosphor-icons';
import ReSelect from 'tio-reimagined/components/select';
import ReSwitch from 'tio-reimagined/components/switch';
import Button from 'tio-reimagined/components/button';
import EmberFlatpickr from 'ember-flatpickr/components/ember-flatpickr';
import { groupFieldOptions } from '../utils/column-utils';
import type { FieldOption } from '../utils/opensearch-field-mapper';

/**
 * A generic dataset filter item used for rendering.
 * Maps a field to its current filter value(s).
 */
interface FilterField extends FieldOption {
  /** Current filter value (for keyword/boolean/text fields) */
  currentValue?: unknown;
  /** Current range start (for date/numeric range fields) */
  rangeStart?: string;
  /** Current range end (for date/numeric range fields) */
  rangeEnd?: string;
}

interface DatasetFiltersSignature {
  Args: {
    /** All filterable fields for the current dataset */
    fields: FieldOption[];
    /** Current filter state (field key → value) */
    filters: Record<string, unknown>;
    /** Callback to update filters */
    onFilterUpdate: (updates: Record<string, unknown>) => void;
    /** Callback to clear all filters */
    onClearFilters: () => void;
    /** Whether fields are still loading */
    isLoading?: boolean;
  };
  Element: HTMLDivElement;
}

/**
 * DatasetFilters — Auto-generates filter controls from field metadata.
 *
 * Renders appropriate filter controls per field type:
 * - keyword fields with enumValues → dropdown (ReSelect)
 * - keyword fields without enumValues → text input
 * - date fields → date range picker (start/end)
 * - boolean fields → toggle switch
 * - integer/long/float fields → min/max number inputs
 */
export class DatasetFilters extends Component<DatasetFiltersSignature> {
  /**
   * Group filterable fields by their group property.
   */
  get groupedFilterableFields(): Array<[string, FilterField[]]> {
    const filterableFields = this.args.fields.filter((f) => f.isFilterable);

    if (filterableFields.length === 0) {
      return [];
    }

    const grouped = groupFieldOptions(filterableFields);

    // Enrich with current filter values
    return grouped.map(([groupName, fields]) => {
      const enriched: FilterField[] = fields.map((field) => ({
        ...field,
        currentValue: this.args.filters[field.key],
        rangeStart: this.args.filters[`${field.key}__gte`] as string | undefined,
        rangeEnd: this.args.filters[`${field.key}__lte`] as string | undefined,
      }));
      return [groupName, enriched] as [string, FilterField[]];
    });
  }

  get hasActiveFilters() {
    return Object.keys(this.args.filters).length > 0;
  }

  get activeFilterCount() {
    return Object.values(this.args.filters).filter((v) => v !== undefined && v !== null && v !== '')
      .length;
  }

  /**
   * Determine which control type to render for a field.
   * Checks backend ui_hints first, then falls back to type-based inference.
   */
  getControlType = (field: FieldOption) => {
    // Backend-provided UI hint takes priority
    const widget = field.uiHints?.filterWidget;
    if (widget) {
      // Map backend widget names to internal control type names
      const widgetMap: Record<string, string> = {
        select: 'select',
        multi_select: 'multi-select',
        typeahead: 'typeahead',
        boolean: 'boolean',
        date_range: 'date',
        number_range: 'number-range',
        company_select: 'select',
        text: 'text',
      };
      const mapped = widgetMap[widget];
      if (mapped) return mapped;
    }

    // Fall back to type-based inference
    const type = field.reportDatatype || field.type;

    if (type === 'boolean') return 'boolean';
    if (type === 'date' || field.isDate) return 'date';
    if (['integer', 'long', 'float', 'double', 'short'].includes(type)) {
      return field.isRangeQueryable ? 'number-range' : 'text';
    }
    if (type === 'keyword' && field.enumValues && field.enumValues.length > 0) {
      return 'select';
    }
    return 'text';
  };

  isControlType = (field: FieldOption, type: string) => {
    return this.getControlType(field) === type;
  };

  /**
   * Build select options from enumValues.
   */
  getSelectOptions = (field: FieldOption) => {
    return (field.enumValues || []).map((v) => ({ key: v, label: v }));
  };

  /**
   * Handle text/keyword filter change via input event.
   */
  handleTextChange = (fieldKey: string, event: Event) => {
    const target = event.target as HTMLInputElement;
    this.args.onFilterUpdate({ [fieldKey]: target.value || undefined });
  };

  /**
   * Handle dropdown select change.
   */
  handleSelectChange = (fieldKey: string, value: string | null) => {
    this.args.onFilterUpdate({ [fieldKey]: value || undefined });
  };

  /**
   * Handle multi-select change. Receives the full array of selected keys.
   */
  handleMultiSelectChange = (fieldKey: string, keys: string[]) => {
    this.args.onFilterUpdate({ [fieldKey]: keys.length > 0 ? keys : undefined });
  };

  /**
   * Get current multi-select value as an array of strings.
   */
  getMultiSelectValue = (value: unknown): string[] => {
    if (Array.isArray(value)) return value.map(String);
    if (typeof value === 'string' && value) return [value];
    return [];
  };

  /**
   * Handle boolean toggle.
   */
  handleBooleanChange = (fieldKey: string, value: boolean) => {
    this.args.onFilterUpdate({ [fieldKey]: value || undefined });
  };

  /**
   * Handle date range start change.
   */
  handleDateStartChange = (fieldKey: string, selectedDates: Date[]) => {
    const date = selectedDates[0];
    const dateString = date ? date.toISOString().split('T')[0] : undefined;
    this.args.onFilterUpdate({ [`${fieldKey}__gte`]: dateString });
  };

  /**
   * Handle date range end change.
   */
  handleDateEndChange = (fieldKey: string, selectedDates: Date[]) => {
    const date = selectedDates[0];
    const dateString = date ? date.toISOString().split('T')[0] : undefined;
    this.args.onFilterUpdate({ [`${fieldKey}__lte`]: dateString });
  };

  /**
   * Handle number range min change.
   */
  handleNumberMinChange = (fieldKey: string, event: Event) => {
    const target = event.target as HTMLInputElement;
    this.args.onFilterUpdate({
      [`${fieldKey}__gte`]: target.value ? Number(target.value) : undefined,
    });
  };

  /**
   * Handle number range max change.
   */
  handleNumberMaxChange = (fieldKey: string, event: Event) => {
    const target = event.target as HTMLInputElement;
    this.args.onFilterUpdate({
      [`${fieldKey}__lte`]: target.value ? Number(target.value) : undefined,
    });
  };

  /**
   * Clear a date range for a specific field.
   */
  clearDateStart = (fieldKey: string) => {
    this.args.onFilterUpdate({ [`${fieldKey}__gte`]: undefined });
  };

  clearDateEnd = (fieldKey: string) => {
    this.args.onFilterUpdate({ [`${fieldKey}__lte`]: undefined });
  };

  /**
   * Safely cast an unknown filter value to a string for template use.
   */
  getStringValue = (value: unknown): string | undefined => {
    if (value === undefined || value === null) return undefined;
    return String(value);
  };

  <template>
    <div class="flex flex-col gap-y-6 text-sm" ...attributes>
      {{#if @isLoading}}
        <div class="flex items-center justify-center py-8">
          <p class="text-sm text-gray-500">Loading available filters…</p>
        </div>
      {{else if (eq this.groupedFilterableFields.length 0)}}
        <div class="flex flex-col items-center justify-center py-8 text-center">
          <PhFunnel @size={{32}} class="text-gray-400 mb-2" />
          <p class="text-sm text-gray-500">No filterable fields available for this dataset.</p>
          <p class="text-xs text-gray-400 mt-1">
            Filters will appear here when the backend provides filterable field metadata.
          </p>
        </div>
      {{else}}
        {{! Clear all filters button }}
        {{#if this.hasActiveFilters}}
          <div class="flex items-center justify-between">
            <span class="text-xs text-gray-500">
              {{this.activeFilterCount}}
              active
              {{if (eq this.activeFilterCount 1) "filter" "filters"}}
            </span>
            <Button
              @appearance="minimal"
              @intent="danger"
              @size="sm"
              {{on "click" @onClearFilters}}
            >
              Clear All Filters
            </Button>
          </div>
        {{/if}}

        {{#each this.groupedFilterableFields as |group|}}
          <fieldset>
            <div class="flex flex-col gap-y-3">
              <h3 class="text-xs font-semibold uppercase tracking-wide text-gray-500">{{get
                  group
                  "0"
                }}</h3>
              <div class="flex flex-col gap-y-3 pb-3 border-b border-gray-200 dark:border-gray-600">
                {{#each (get group "1") as |field|}}
                  {{#if (this.isControlType field "select")}}
                    {{! Keyword field with enum values — dropdown }}
                    <ReSelect
                      @label={{field.label}}
                      @items={{this.getSelectOptions field}}
                      @selected={{this.getStringValue field.currentValue}}
                      @onChange={{fn this.handleSelectChange field.key}}
                      @clearable={{true}}
                      @searchable={{true}}
                      @placeholder="All"
                      @size="sm"
                    />
                  {{else if (this.isControlType field "multi-select")}}
                    {{! Multi-select field — dropdown with multiple selection }}
                    <ReSelect
                      @label={{field.label}}
                      @items={{this.getSelectOptions field}}
                      @multiple={{true}}
                      @selectedKeys={{this.getMultiSelectValue field.currentValue}}
                      @onChangeKeys={{fn this.handleMultiSelectChange field.key}}
                      @clearable={{true}}
                      @searchable={{true}}
                      @placeholder="Select one or more…"
                      @size="sm"
                    />
                  {{else if (this.isControlType field "typeahead")}}
                    {{! Typeahead field — text search input for large value sets }}
                    <div class="flex flex-col gap-1">
                      <label
                        for={{field.key}}
                        class="text-sm font-medium text-gray-900"
                      >{{field.label}}</label>
                      <input
                        id={{field.key}}
                        type="text"
                        value={{this.getStringValue field.currentValue}}
                        class="tio-form-input"
                        placeholder="Search {{field.label}}…"
                        {{on "change" (fn this.handleTextChange field.key)}}
                      />
                    </div>
                  {{else if (this.isControlType field "boolean")}}
                    {{! Boolean field — toggle switch }}
                    <ReSwitch
                      @label={{field.label}}
                      @checked={{if field.currentValue true false}}
                      @onChange={{fn this.handleBooleanChange field.key}}
                      @size="sm"
                    />
                  {{else if (this.isControlType field "date")}}
                    {{! Date field — date range picker }}
                    <div class="flex flex-col gap-2">
                      <label class="text-sm font-medium text-gray-900">{{field.label}}</label>
                      <div class="flex items-center gap-2">
                        <span class="text-sm text-gray-700">From</span>
                        <div class="flex-1 relative">
                          {{! @glint-expect-error: Flatpickr type mismatch }}
                          <EmberFlatpickr
                            @date={{if field.rangeStart field.rangeStart ""}}
                            @onChange={{fn this.handleDateStartChange field.key}}
                            @dateFormat="Y-m-d"
                            @altInput={{true}}
                            @altFormat="F j, Y"
                            placeholder="mm/dd/yyyy"
                            class="tio-form-input"
                          />
                          {{#if field.rangeStart}}
                            <button
                              type="button"
                              {{on "click" (fn this.clearDateStart field.key)}}
                              class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                              aria-label="Clear start date"
                            >
                              <PhX @size="16" />
                            </button>
                          {{/if}}
                        </div>
                        <span class="text-sm text-gray-700">To</span>
                        <div class="flex-1 relative">
                          {{! @glint-expect-error: Flatpickr type mismatch }}
                          <EmberFlatpickr
                            @date={{if field.rangeEnd field.rangeEnd ""}}
                            @onChange={{fn this.handleDateEndChange field.key}}
                            @dateFormat="Y-m-d"
                            @altInput={{true}}
                            @altFormat="F j, Y"
                            placeholder="mm/dd/yyyy"
                            @minDate={{if field.rangeStart field.rangeStart ""}}
                            class="tio-form-input"
                          />
                          {{#if field.rangeEnd}}
                            <button
                              type="button"
                              {{on "click" (fn this.clearDateEnd field.key)}}
                              class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                              aria-label="Clear end date"
                            >
                              <PhX @size="16" />
                            </button>
                          {{/if}}
                        </div>
                      </div>
                    </div>
                  {{else if (this.isControlType field "number-range")}}
                    {{! Numeric range field — min/max inputs }}
                    <div class="flex flex-col gap-2">
                      <label class="text-sm font-medium text-gray-900">{{field.label}}</label>
                      <div class="flex items-center gap-2">
                        <label for="{{field.key}}-min" class="text-sm text-gray-700">Min</label>
                        <input
                          id="{{field.key}}-min"
                          type="number"
                          value={{field.rangeStart}}
                          class="tio-form-input flex-1"
                          placeholder="Min"
                          {{on "change" (fn this.handleNumberMinChange field.key)}}
                        />
                        <label for="{{field.key}}-max" class="text-sm text-gray-700">Max</label>
                        <input
                          id="{{field.key}}-max"
                          type="number"
                          value={{field.rangeEnd}}
                          class="tio-form-input flex-1"
                          placeholder="Max"
                          {{on "change" (fn this.handleNumberMaxChange field.key)}}
                        />
                      </div>
                    </div>
                  {{else}}
                    {{! Text/keyword field — text input }}
                    <div class="flex flex-col gap-1">
                      <label
                        for={{field.key}}
                        class="text-sm font-medium text-gray-900"
                      >{{field.label}}</label>
                      <input
                        id={{field.key}}
                        type="text"
                        value={{this.getStringValue field.currentValue}}
                        class="tio-form-input"
                        placeholder="Filter by {{field.label}}…"
                        {{on "change" (fn this.handleTextChange field.key)}}
                      />
                    </div>
                  {{/if}}
                {{/each}}
              </div>
            </div>
          </fieldset>
        {{/each}}
      {{/if}}
    </div>
  </template>
}

export default DatasetFilters;
